import { useState, useEffect } from "react";
import { Outlet, useNavigate, useSearchParams } from "react-router-dom";
import { AppHeader } from "./AppHeader";
import { ErrorBoundary } from "./ErrorBoundary";
import { LeftConsole } from "./LeftConsole";
import { useActiveNearby } from "@/hooks/useActiveNearby";
import { useSuggestedUsers } from "@/hooks/useSuggestedUsers";
import { useMessageThreads } from "@/hooks/useMessageThreads";
import { isFeatureEnabled } from "@/config/featureFlags";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";

export const AppShell = () => {
  const leftConsoleEnabled = true; // Force enable for debugging
  const [isLeftRailCollapsed, setIsLeftRailCollapsed] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  console.log("AppShell - leftConsoleEnabled:", leftConsoleEnabled);
  
  // Set up auth state
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setUser(session?.user ?? null);
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);
  
  // Fetch real data (only if feature enabled)
  const { data: activeNearby = [] } = useActiveNearby(undefined, undefined, 25, 20);
  const { data: suggested = [] } = useSuggestedUsers(20);
  const { threads, loading: threadsLoading } = useMessageThreads();
  
  // TODO: fetch real notifications
  const notifications = [
    { id:"n1", kind:"mention" as const, title:"@you in #cocoa", preview:"Can you verify price?", ts:new Date().toISOString(), unread:true, link:"/posts/123" },
  ];

  // Fallback layout without LeftConsole
  if (!leftConsoleEnabled) {
    return (
      <div className="min-h-[100svh] flex flex-col bg-background text-foreground">
        <AppHeader />
        <main id="app-main" className="flex-1 w-full md:max-w-3xl md:mx-auto">
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-[100svh] flex flex-col bg-background text-foreground">
      <AppHeader isLeftRailCollapsed={isLeftRailCollapsed} />
      <div className="flex-1 flex">
        <LeftConsole
          className="flex-none"
          activeNearby={activeNearby}
          suggested={suggested}
          notifications={notifications}
          threads={threads}
          onFollow={(id)=>console.log("follow", id)}
          onMessage={(id)=>console.log("DM", id)}
          onCollapsedChange={setIsLeftRailCollapsed}
          user={user}
          onNavigateToDiscover={() => {
            setSearchParams({ tab: 'discover', view: 'teams' });
          }}
        />
        <main id="app-main" className="flex-1 w-full">
          <div className="w-full md:max-w-3xl md:mx-auto">
            <ErrorBoundary>
              <Outlet context={{ isLeftRailCollapsed }} />
            </ErrorBoundary>
          </div>
        </main>
      </div>
    </div>
  );
};
